import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchTemplates, createTemplate, updateTemplate, deleteTemplate, CampaignTemplate } from '@/services/campaignTemplateService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useCampaignTemplates(companyId: string | undefined) {
  return useQuery({
    queryKey: ['campaign-templates', companyId],
    queryFn: () => fetchTemplates(companyId!),
    enabled: !!companyId,
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-templates'] });
      toast.success('Template created');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CampaignTemplate> }) => updateTemplate(id, updates),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-templates'] });
      toast.success('Template updated');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign-templates'] });
      toast.success('Template deleted');
    },
    onError: (e: any) => toast.error(e.message),
  });
}

export function useSeedDefaultTemplates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (companyId: string) => {
      const { data, error } = await supabase.functions.invoke('seed-default-templates', {
        body: { company_id: companyId },
      });
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error || 'Seeding failed');
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['campaign-templates'] });
      if (data.seeded) {
        toast.success('Default templates created');
      }
    },
    onError: (e: any) => toast.error(e.message),
  });
}
